import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { HiRefresh } from 'react-icons/hi';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';

import api from '../../services/api';
import { ContainerTemplates, TemplateCard, ConfigSection } from './style.js';

import Menu from '../../components/Menu';
import Footer from '../../components/Footer';
import AuthGuard from '../../components/AuthGuard';
import ButtonDefault from '../../components/ButtonDefault';
// import SelectDefault from '../../components/SelectDefault';
import InputMask from 'react-input-mask';
import { cpf as cpfValidator } from 'cpf-cnpj-validator';
import isEmail from 'validator/lib/isEmail';

const D4SignTemplates = () => {
  document.title = 'Enermerco - Gerenciamento de Templates D4Sign';

  const [loading, setLoading] = useState(false);
  const [templatesData, setTemplatesData] = useState({});
  const [currentConfig, setCurrentConfig] = useState({});
  const [selectedPFTemplate, setSelectedPFTemplate] = useState('');
  const [selectedPJTemplate, setSelectedPJTemplate] = useState('');
  const [pfDescription, setPfDescription] = useState('');
  const [pjDescription, setPjDescription] = useState('');

  // Estado: Dados do Assinante (Diretor do Clube)
  const [directorName, setDirectorName] = useState('');
  const [directorCpf, setDirectorCpf] = useState('');
  const [directorEmail, setDirectorEmail] = useState('');
  const [savingDirector, setSavingDirector] = useState(false);
  // Metadados dos parâmetros (id, info, type) para update/criação
  const [directorParamsMeta, setDirectorParamsMeta] = useState({
    D4SIGN_DIRECTOR_NAME: {
      id: null,
      info: 'Nome do diretor do clube (assinante D4Sign)',
      type: 'STRING'
    },
    D4SIGN_DIRECTOR_CPF: {
      id: null,
      info: 'CPF do diretor do clube (assinante D4Sign)',
      type: 'STRING'
    },
    D4SIGN_DIRECTOR_EMAIL: {
      id: null,
      info: 'E-mail do diretor do clube (assinante D4Sign)',
      type: 'STRING'
    }
  });

  useEffect(() => {
    loadTemplates();
    loadCurrentConfig();
    loadDirectorParams();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('d4sign/templates');
      if (response.data.data) {
        setTemplatesData(response.data.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar templates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Carrega parâmetros do diretor do clube (assinante)
  const loadDirectorParams = async () => {
    try {
      const [rName, rCpf, rEmail] = await Promise.all([
        api.get('parameter', { params: { key: 'D4SIGN_DIRECTOR_NAME' } }),
        api.get('parameter', { params: { key: 'D4SIGN_DIRECTOR_CPF' } }),
        api.get('parameter', { params: { key: 'D4SIGN_DIRECTOR_EMAIL' } })
      ]);

      const pName = Array.isArray(rName.data?.data) && rName.data.data.length > 0 ? rName.data.data[0] : null;
      const pCpf = Array.isArray(rCpf.data?.data) && rCpf.data.data.length > 0 ? rCpf.data.data[0] : null;
      const pEmail = Array.isArray(rEmail.data?.data) && rEmail.data.data.length > 0 ? rEmail.data.data[0] : null;

      if (pName) setDirectorName(pName.DS_VALOR_PARAMETRO || '');
      if (pCpf) setDirectorCpf(pCpf.DS_VALOR_PARAMETRO || '');
      if (pEmail) setDirectorEmail(pEmail.DS_VALOR_PARAMETRO || '');

      setDirectorParamsMeta((prev) => ({
        D4SIGN_DIRECTOR_NAME: {
          id: pName?.ID_DM990 ?? prev.D4SIGN_DIRECTOR_NAME.id,
          info: pName?.DS_INF_PARAMETRO || prev.D4SIGN_DIRECTOR_NAME.info,
          type: pName?.TP_PARAMETRO || prev.D4SIGN_DIRECTOR_NAME.type
        },
        D4SIGN_DIRECTOR_CPF: {
          id: pCpf?.ID_DM990 ?? prev.D4SIGN_DIRECTOR_CPF.id,
          info: pCpf?.DS_INF_PARAMETRO || prev.D4SIGN_DIRECTOR_CPF.info,
          type: pCpf?.TP_PARAMETRO || prev.D4SIGN_DIRECTOR_CPF.type
        },
        D4SIGN_DIRECTOR_EMAIL: {
          id: pEmail?.ID_DM990 ?? prev.D4SIGN_DIRECTOR_EMAIL.id,
          info: pEmail?.DS_INF_PARAMETRO || prev.D4SIGN_DIRECTOR_EMAIL.info,
          type: pEmail?.TP_PARAMETRO || prev.D4SIGN_DIRECTOR_EMAIL.type
        }
      }));
    } catch (error) {
      console.error('Erro ao carregar parâmetros do diretor:', error);
      toast.error('Não foi possível carregar os dados do assinante');
    }
  };

  const onlyDigits = (v = '') => (v || '').replace(/\D+/g, '');

  const isDirectorFormValid = () => {
    const nameValid = (directorName || '').trim().length >= 3;
    const cpfDigits = onlyDigits(directorCpf);
    const cpfValid = cpfValidator.isValid(cpfDigits);
    const emailValid = isEmail((directorEmail || '').trim());
    return nameValid && cpfValid && emailValid;
  };

  // Cria/Atualiza um parâmetro específico com fallback nas rotas corretas
  const upsertParam = async ({ id, key, value, info = '', type = 'STRING' }) => {
    const body = {
      DS_KEY_PARAMETRO: key,
      DS_VALOR_PARAMETRO: value,
      DS_INF_PARAMETRO: info,
      TP_PARAMETRO: type
    };
    // 1) Tenta atualizar se houver ID
    if (id) {
      try {
        const resp = await api.put(`parameter?id=${id}`, body);
        return resp;
      } catch (e) {
        // segue para tentativa de criação
      }
    }
    // 2) Tenta criar
    return api.post('parameter', body);
  };

  const handleSaveDirectorData = async () => {
    if (!isDirectorFormValid()) {
      toast.warning('Preencha os dados do assinante corretamente (Nome, CPF e E-mail).');
      return;
    }
    setSavingDirector(true);
    try {
      const cpfDigits = onlyDigits(directorCpf);
      const updates = [
        {
          id: directorParamsMeta.D4SIGN_DIRECTOR_NAME.id,
          key: 'D4SIGN_DIRECTOR_NAME',
          value: directorName,
          info: directorParamsMeta.D4SIGN_DIRECTOR_NAME.info,
          type: directorParamsMeta.D4SIGN_DIRECTOR_NAME.type
        },
        {
          id: directorParamsMeta.D4SIGN_DIRECTOR_CPF.id,
          key: 'D4SIGN_DIRECTOR_CPF',
          value: cpfDigits,
          info: directorParamsMeta.D4SIGN_DIRECTOR_CPF.info,
          type: directorParamsMeta.D4SIGN_DIRECTOR_CPF.type
        },
        {
          id: directorParamsMeta.D4SIGN_DIRECTOR_EMAIL.id,
          key: 'D4SIGN_DIRECTOR_EMAIL',
          value: directorEmail,
          info: directorParamsMeta.D4SIGN_DIRECTOR_EMAIL.info,
          type: directorParamsMeta.D4SIGN_DIRECTOR_EMAIL.type
        }
      ];

      for (const u of updates) {
        // eslint-disable-next-line no-await-in-loop
        await upsertParam(u);
      }

      toast.success('Dados do assinante salvos com sucesso!');
      loadDirectorParams();
    } catch (error) {
      console.error('Erro ao salvar dados do assinante:', error);
      toast.error('Erro ao salvar dados do assinante');
    } finally {
      setSavingDirector(false);
    }
  };

  const loadCurrentConfig = async () => {
    try {
      const response = await api.get('d4sign/templates/current');
      if (response.data.data) {
        setCurrentConfig(response.data.data);
        if (response.data.data.PF_template) {
          setSelectedPFTemplate(response.data.data.PF_template.id);
          setPfDescription(response.data.data.PF_template.description || '');
        }
        if (response.data.data.PJ_template) {
          setSelectedPJTemplate(response.data.data.PJ_template.id);
          setPjDescription(response.data.data.PJ_template.description || '');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configuração atual:', error);
    }
  };

  const handleSaveTemplates = async () => {
    if (!selectedPFTemplate || !selectedPJTemplate) {
      toast.error('Selecione ambos os templates (PF e PJ)');
      return;
    }

    setLoading(true);
    try {
      const data = {
        pf_template_id: selectedPFTemplate,
        pj_template_id: selectedPJTemplate,
        pf_description: pfDescription,
        pj_description: pjDescription
      };

      await api.post('d4sign/templates/set', data);
      toast.success('Templates configurados com sucesso!');
      loadCurrentConfig();
    } catch (error) {
      toast.error('Erro ao salvar configuração dos templates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderTemplateDetails = (templateId) => {
    const template = Object.values(templatesData).find(t => t.id === templateId);
    if (!template) return null;

    return (
      <div className="mt-2">
        <small className="text-muted">
          <strong>Nome:</strong> {template.name}<br/>
          <strong>Tipo:</strong> {template.type}<br/>
          {template.variables && template.variables.tokens_gerais && (
            <>
              <strong>Variáveis:</strong> {template.variables.tokens_gerais.join(', ')}
            </>
          )}
        </small>
      </div>
    );
  };

  return (
    <AuthGuard>
      <Menu />
      <ContainerTemplates>
        <Container fluid>
          <Row>
            <Col>
              <div className="page-header">
                <h2>Gerenciamento de Templates D4Sign</h2>
                <p className="text-muted">
                  Configure os templates para assinatura de documentos de Pessoa Física e Pessoa Jurídica
                </p>
              </div>

              {/* Dados do Assinante no topo */}
              <ConfigSection>
                {/* Dados do Assinante (Diretor do Clube) */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Dados do Assinante (Diretor do Clube)</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6} className="mb-3">
                        <label className="form-label">Nome do Diretor</label>
                        <input
                          type="text"
                          className="form-control"
                          value={directorName}
                          onChange={(e) => setDirectorName(e.target.value)}
                          placeholder="Ex: João da Silva"
                        />
                      </Col>
                      <Col md={3} className="mb-3">
                        <label className="form-label">CPF do Diretor</label>
                        <InputMask
                          mask="999.999.999-99"
                          value={directorCpf}
                          onChange={(e) => setDirectorCpf(e.target.value)}
                        >
                          {(inputProps) => (
                            <input
                              type="text"
                              className="form-control"
                              placeholder="000.000.000-00"
                              {...inputProps}
                            />
                          )}
                        </InputMask>
                      </Col>
                      <Col md={3} className="mb-3">
                        <label className="form-label">E-mail do Diretor</label>
                        <input
                          type="email"
                          className="form-control"
                          value={directorEmail}
                          onChange={(e) => setDirectorEmail(e.target.value)}
                          placeholder="diretor@clube.com.br"
                        />
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-2">
                      <ButtonDefault
                        Text={savingDirector ? 'Salvando...' : 'Salvar Dados do Assinante'}
                        Width="260px"
                        Margin="0"
                        onClick={handleSaveDirectorData}
                        disabled={savingDirector || !isDirectorFormValid()}
                        style={{
                          backgroundColor: savingDirector || !isDirectorFormValid() ? 'var(--gray-500)' : 'var(--primary-color)',
                          cursor: savingDirector || !isDirectorFormValid() ? 'not-allowed' : 'pointer'
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>

                {/* Configuração Atual */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Configuração Atual</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <h6>Template Pessoa Física (PF)</h6>
                        {currentConfig.PF_template ? (
                          <div>
                            <Badge bg="success" className="me-2">Configurado</Badge>
                            <p className="mb-1"><strong>ID:</strong> {currentConfig.PF_template.id}</p>
                            <p className="mb-0"><strong>Descrição:</strong> {currentConfig.PF_template.description}</p>
                          </div>
                        ) : (
                          <Badge bg="warning">Não configurado</Badge>
                        )}
                      </Col>
                      <Col md={6}>
                        <h6>Template Pessoa Jurídica (PJ)</h6>
                        {currentConfig.PJ_template ? (
                          <div>
                            <Badge bg="success" className="me-2">Configurado</Badge>
                            <p className="mb-1"><strong>ID:</strong> {currentConfig.PJ_template.id}</p>
                            <p className="mb-0"><strong>Descrição:</strong> {currentConfig.PJ_template.description}</p>
                          </div>
                        ) : (
                          <Badge bg="warning">Não configurado</Badge>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

              </ConfigSection>

              {/* Configuração de Templates */}
              <Card className="mb-4">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Configurar Templates</h5>
                    <Button 
                      onClick={loadTemplates}
                      disabled={loading}
                      style={{
                        backgroundColor: loading ? 'var(--gray-100)' : 'var(--bg-light)',
                        borderColor: loading ? 'var(--gray-500)' : 'var(--primary-color)',
                        color: loading ? 'var(--gray-500)' : 'var(--primary-color)',
                        borderRadius: 'var(--border-radius-md)',
                        fontWeight: 'var(--font-weight-medium)',
                        padding: 'var(--spacing-sm) var(--spacing-lg)',
                        border: '2px solid',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.target.style.backgroundColor = 'var(--primary-color)';
                          e.target.style.color = 'var(--text-light)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) {
                          e.target.style.backgroundColor = 'var(--bg-light)';
                          e.target.style.color = 'var(--primary-color)';
                        }
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Carregando...
                        </>
                      ) : (
                        <>
                          <HiRefresh className="me-2" size={16} />
                          Atualizar Lista
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" />
                      <p className="mt-2">Carregando templates...</p>
                    </div>
                  ) : (
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Template Pessoa Física (PF)</label>
                          <select
                            className="form-control"
                            value={selectedPFTemplate}
                            onChange={(e) => setSelectedPFTemplate(e.target.value)}
                          >
                            <option value="">Selecione um template...</option>
                            {Object.entries(templatesData).map(([key, template]) => (
                              <option key={template.id} value={template.id}>
                                {template.name} (ID: {template.id})
                              </option>
                            ))}
                          </select>
                          {selectedPFTemplate && renderTemplateDetails(selectedPFTemplate)}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Descrição PF (opcional)</label>
                          <input
                            type="text"
                            className="form-control"
                            value={pfDescription}
                            onChange={(e) => setPfDescription(e.target.value)}
                            placeholder="Ex: Template de Adesão - Pessoa Física"
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Template Pessoa Jurídica (PJ)</label>
                          <select
                            className="form-control"
                            value={selectedPJTemplate}
                            onChange={(e) => setSelectedPJTemplate(e.target.value)}
                          >
                            <option value="">Selecione um template...</option>
                            {Object.entries(templatesData).map(([key, template]) => (
                              <option key={template.id} value={template.id}>
                                {template.name} (ID: {template.id})
                              </option>
                            ))}
                          </select>
                          {selectedPJTemplate && renderTemplateDetails(selectedPJTemplate)}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Descrição PJ (opcional)</label>
                          <input
                            type="text"
                            className="form-control"
                            value={pjDescription}
                            onChange={(e) => setPjDescription(e.target.value)}
                            placeholder="Ex: Template de Adesão - Pessoa Jurídica"
                          />
                        </div>
                      </Col>
                    </Row>
                  )}
                  
                  <div className="d-flex justify-content-end mt-4">
                    <ButtonDefault
                      Text={loading ? 'Salvando...' : 'Salvar Configuração'}
                      Width="200px"
                      Margin="0"
                      onClick={handleSaveTemplates}
                      disabled={loading || !selectedPFTemplate || !selectedPJTemplate}
                      style={{
                        backgroundColor: loading || !selectedPFTemplate || !selectedPJTemplate ? 'var(--gray-500)' : 'var(--primary-color)',
                        cursor: loading || !selectedPFTemplate || !selectedPJTemplate ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>

              {/* Lista de Templates Disponíveis */}
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Templates Disponíveis no D4Sign</h5>
                </Card.Header>
                <Card.Body>
                  {Object.keys(templatesData).length === 0 ? (
                    <p className="text-muted">Nenhum template encontrado. Clique em "Atualizar Lista" para carregar.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table striped hover>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Nome</th>
                            <th>Tipo</th>
                            <th>Variáveis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(templatesData).map(([key, template]) => (
                            <tr key={key}>
                              <td>
                                <code>{template.id}</code>
                              </td>
                              <td>{template.name}</td>
                              <td>
                                <Badge bg="info">{template.type}</Badge>
                              </td>
                              <td>
                                {template.variables && template.variables.tokens_gerais ? (
                                  <small className="text-muted">
                                    {template.variables.tokens_gerais.join(', ')}
                                  </small>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </ContainerTemplates>
      <Footer />
      <ToastContainer />
    </AuthGuard>
  );
};

export default D4SignTemplates;
